/**
 * Get the full URL for an uploaded image with optimization parameters
 * @param {string|object} image - Image path string or image object with url property
 * @param {object} options - Optimization options
 * @returns {string} Full URL to the image with optimization parameters
 */
export const getImageUrl = (image, options = {}) => {
  // If no image provided, return empty string
  if (!image) return '';

  const {
    quality = 85,
    width,
    height,
    format = 'auto'
  } = options;

  // Get the API base URL from environment or default to localhost:5000
  const API_BASE_URL = import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:5000';

  // If image is an object, extract the url property
  let imagePath = '';
  if (typeof image === 'object') {
    imagePath = image.url || image.thumbnail || '';
  } else {
    imagePath = image;
  }

  // If imagePath is empty, return empty string
  if (!imagePath) return '';

  // If the path already includes http/https
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    // Handle Cloudinary URLs optimization
    if (imagePath.includes('cloudinary.com') && imagePath.includes('/upload/')) {
      const transforms = [];
      
      // Format
      if (!imagePath.includes('f_')) {
        if (format === 'auto') {
          transforms.push('f_auto');
        } else if (format) {
          transforms.push(`f_${format}`);
        }
      }
      
      // Quality
      if ((quality === 85 || quality === 'auto') && !imagePath.includes('q_')) {
        transforms.push('q_auto');
      }
      
      // Width
      if (width && !imagePath.includes('w_')) {
        transforms.push(`w_${width}`);
      }
      
      // Height
      if (height && !imagePath.includes('h_')) {
        transforms.push(`h_${height}`);
      }

      if (transforms.length > 0) {
        return imagePath.replace('/upload/', `/upload/${transforms.join(',')}/`);
      }
    }
    return imagePath;
  }

  // If the path starts with /, remove it to avoid double slashes
  const cleanPath = imagePath.startsWith('/') ? imagePath.slice(1) : imagePath;

  // Construct the full URL
  const baseUrl = `${API_BASE_URL}/${cleanPath}`;
  
  // Add optimization parameters
  const params = new URLSearchParams();
  if (quality !== 85) params.append('q', quality);
  if (width) params.append('w', width);
  if (height) params.append('h', height);
  if (format !== 'auto') params.append('format', format);
  
  const queryString = params.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
};

/**
 * Get thumbnail URL with fallback to main image and optimization
 * @param {object} image - Image object with url and optional thumbnail
 * @param {object} options - Optimization options
 * @returns {string} Full URL to the thumbnail or main image
 */
export const getThumbnailUrl = (image, options = {}) => {
  if (!image) return '';
  
  // Try thumbnail first, then url
  const imagePath = image.thumbnail || image.url || '';
  
  if (!imagePath) return '';
  
  // Use the optimized getImageUrl function
  return getImageUrl(imagePath, { quality: 70, ...options });
};

/**
 * Generate responsive image srcset for different screen sizes
 * @param {string} imageSrc - Base image source
 * @param {array} sizes - Array of widths [320, 640, 1024, 1920]
 * @returns {string} srcset string for responsive images
 */
export const generateSrcSet = (imageSrc, sizes = [320, 640, 1024, 1920]) => {
  if (!imageSrc) return '';
  
  return sizes
    .map(size => `${getImageUrl(imageSrc, { width: size, quality: 85 })} ${size}w`)
    .join(', ');
};

/**
 * Check if browser supports WebP format
 * @returns {boolean} True if WebP is supported
 */
export const supportsWebP = () => {
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
};

/**
 * Preload critical images for better performance
 * @param {array} imageSources - Array of image URLs to preload
 */
export const preloadImages = (imageSources) => {
  imageSources.forEach(src => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = src;
    document.head.appendChild(link);
  });
};
